import type { ModuleCategory } from "@/types/modules";

export type CategorizedItem = {
  status?: string;
  categoryId?: string;
};

export type ResolvedCategories = {
  categories: ModuleCategory[];
  free: boolean;
};

export function resolveCategoryColor(
  categoryId: string | undefined,
  categories: ModuleCategory[] | undefined,
  fallback: string,
): string {
  if (!categoryId || !categories) return fallback;
  return categories.find((c) => c.id === categoryId)?.color ?? fallback;
}

export function resolveCategoryLabel(
  categoryId: string | undefined,
  categories: ModuleCategory[] | undefined,
  fallback: string,
): string {
  if (!categoryId || !categories) return fallback;
  return categories.find((c) => c.id === categoryId)?.label ?? fallback;
}

export function buildCategoryLegendItems(
  items: Array<{ categoryId?: string }>,
  categories: ModuleCategory[] | undefined,
): Array<{ id: string; label: string; color: string; value: number }> | null {
  if (!categories) return null;
  return categories.map((cat) => ({
    id: cat.id,
    label: cat.label,
    color: cat.color,
    value: items.filter((it) => it.categoryId === cat.id).length,
  }));
}

export function resolveCategories(
  categories: ModuleCategory[] | undefined,
  legacyCategories: ModuleCategory[],
): ResolvedCategories {
  const free = Boolean(categories && categories.length > 0);
  return {
    categories: free ? (categories as ModuleCategory[]) : legacyCategories,
    free,
  };
}

export function categoryKeyOf(
  item: CategorizedItem | undefined,
  free: boolean,
): string | undefined {
  if (!item) return undefined;
  return free ? item.categoryId : item.status;
}

export function buildResolvedLegendItems(
  items: CategorizedItem[],
  resolved: ResolvedCategories,
): Array<{ id: string; label: string; color: string; value: number }> {
  return resolved.categories.map((cat) => ({
    id: cat.id,
    label: cat.label,
    color: cat.color,
    value: items.filter((it) => categoryKeyOf(it, resolved.free) === cat.id).length,
  }));
}

export function legendIndexOfCategory(
  legendItems: Array<{ id?: string }>,
  categoryId: string | null | undefined,
): number | null {
  if (!categoryId) return null;
  const index = legendItems.findIndex((it) => it.id === categoryId);
  return index === -1 ? null : index;
}

export function categorySvgKey(categoryId: string): string {
  return categoryId.replace(/[^a-zA-Z0-9_-]/g, "-");
}

export function hasCategoryGrouping(
  items: CategorizedItem[],
  categories: ModuleCategory[] | undefined,
): boolean {
  return (
    Boolean(categories && categories.length > 0) ||
    items.some((it) => it.status !== undefined || it.categoryId !== undefined)
  );
}
